import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FiPlay,
  FiPause,
  FiRotateCcw,
  FiVolume2,
  FiMusic,
  FiTarget,
  FiSearch,
} from "react-icons/fi";
import { TbMetronome } from "react-icons/tb";

import { ISongAnalysis } from "./useSongAnalysis";
import * as S from "./styledComponents";

interface IBeatVisualizerProps {
  previewUrl: string;
  analysis: ISongAnalysis;
  chosenDropMs: number;
  beatPhaseOffset: number; // bars
  leadBars: number;
  onChooseDrop: (ms: number) => void;
}

const ZOOM_SPAN_MS = 30000; // the zoomed timeline always shows a static 30s window
// metronome clicks are scheduled this far ahead of the playhead each frame —
// comfortably more than a frame at any refresh rate, small enough that seeks
// and tuning changes stay responsive
const METRO_LOOKAHEAD_MS = 120;

// core colours: build-up = grey, the drop bar bursts gold → green, then green
const GOLD = [255, 209, 71];
const GREEN = [78, 189, 148];
const GRAY = [150, 158, 168];

const mix = (a: number[], b: number[], t: number) =>
  a.map((v, i) => Math.round(v + (b[i] - v) * t));
const rgb = (c: number[]) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`;

// core colour as a function of playhead position vs the drop bar (`anchor`)
const colorStr = (t: number, anchor: number, barDurMs: number): string => {
  if (t < anchor) return rgb(GRAY);
  const into = t - anchor;
  if (into < barDurMs) return rgb(mix(GOLD, GREEN, into / barDurMs));
  return rgb(GREEN);
};

const formatTime = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}:${String(totalSeconds % 60).padStart(2, "0")}`;
};

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

// core base size: half during (and before) the build-up, growing linearly to
// full size at the drop, full from there on
const baseScale = (ms: number, anchor: number, leadStart: number): number => {
  if (ms >= anchor) return 1;
  if (ms <= leadStart || anchor <= leadStart) return 0.5;
  return 0.5 + (0.5 * (ms - leadStart)) / (anchor - leadStart);
};

export const BeatVisualizer = ({
  previewUrl,
  analysis,
  chosenDropMs,
  beatPhaseOffset,
  leadBars,
  onChooseDrop,
}: IBeatVisualizerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const rafRef = useRef<number | undefined>(undefined);
  const coreRef = useRef<HTMLDivElement>(null);
  const ovPlayRef = useRef<HTMLDivElement>(null);
  const zoomPlayRef = useRef<HTMLDivElement>(null);
  const msRef = useRef(0);
  const lastBarIdxRef = useRef<number | null>(null);
  const lastSecRef = useRef(-1);
  const pulseIdRef = useRef(0);
  const playingRef = useRef(false);
  // live geometry read by the (mount-once) RAF loop, so tuning changes never
  // tear the loop down or leave it running a stale closure
  const zoomTrackRef = useRef<HTMLDivElement>(null);
  const zoomBarRef = useRef<HTMLDivElement>(null);
  const barWidthRef = useRef(0); // scrub-bar width in px (ResizeObserver-fed)
  const geomRef = useRef({
    anchor: 0,
    beatDurMs: 1,
    barDurMs: 1,
    duration: 1,
    zoomSpan: 1,
    leadStart: 0,
  });

  // metronome: clicks come from a separate AudioContext (the <audio> element is
  // not routed through it — presigned B2 URLs would need CORS for that); the RAF
  // loop schedules clicks a short lookahead past the playhead each frame
  const audioCtxRef = useRef<AudioContext | null>(null);
  const metroOnRef = useRef(false);
  const metroSchedToRef = useRef(0); // song-ms clicks are scheduled up to
  // audio.currentTime only updates per render quantum, so raw reads lag the true
  // position by 0..~20ms — which would make every click land late by that much.
  // Track the max of (currentTime − performance.now()) while playing: it converges
  // on the freshest reading and gives a de-jittered song clock.
  const mediaClockOffsetRef = useRef(-Infinity);

  // metronome/song volume ratio (0 = song only, 100 = clicks only, 50 = both full);
  // the ref feeds the RAF click scheduler, the state drives the slider + song volume
  const mixRatioRef = useRef(50);

  const [playing, setPlaying] = useState(false);
  const [metronome, setMetronome] = useState(false);
  const [mixRatio, setMixRatio] = useState(50);
  // armed: the next click on a scrub bar picks the nearest bar as the drop
  const [pickingDrop, setPickingDrop] = useState(false);
  const [timeMs, setTimeMs] = useState(0); // time label only — bumped ~1/sec
  const [pulse, setPulse] = useState<{
    id: number;
    color: string;
    base: number;
  } | null>(null);

  const beatDurMs = analysis.beat_period_ms;
  const barDurMs = beatDurMs * 4;
  const duration = analysis.duration_ms || 1;
  const anchor = chosenDropMs + beatPhaseOffset * barDurMs;
  // build-up / restart point measured from the aligned drop (anchor)
  const leadStart = Math.max(0, anchor - leadBars * barDurMs);

  // drop candidates: the analyzer's detected drop + the alternatives, deduped
  const drops = useMemo(() => {
    const set = new Set<number>();
    if (analysis.first_beat_ms != null)
      set.add(Math.round(analysis.first_beat_ms));
    (analysis.drop_candidates_ms || []).forEach((ms) =>
      set.add(Math.round(ms)),
    );
    return Array.from(set).sort((a, b) => a - b);
  }, [analysis]);

  // zoomed view: a playhead-centred 30s window scrolls with playback
  const zoomSpan = Math.max(1, Math.min(duration, ZOOM_SPAN_MS));
  geomRef.current = {
    anchor,
    beatDurMs,
    barDurMs,
    duration,
    zoomSpan,
    leadStart,
  };

  // where the scrolling window starts for a given playhead (clamped at the ends)
  const zoomViewStart = (ms: number) => {
    const { duration, zoomSpan } = geomRef.current;
    return clamp(ms - zoomSpan / 2, 0, Math.max(0, duration - zoomSpan));
  };

  // Paint everything time-driven (core scale/colour/glow + both playheads)
  // imperatively, so playback runs at the display's refresh rate with no
  // per-frame React render. Recreated when geometry changes (tuning).
  // paints one frame from `ms`; reads live geometry from the ref so it's stable
  const paintFrame = useCallback((ms: number) => {
    const { anchor, beatDurMs, barDurMs, duration, zoomSpan, leadStart } =
      geomRef.current;
    const core = coreRef.current;
    if (core) {
      const rel = ms - anchor;
      const beatN = Math.floor(rel / beatDurMs);
      const sinceBeat = rel - beatN * beatDurMs;
      const isBar = ((beatN % 4) + 4) % 4 === 0;
      const amp = isBar ? 0.3 : 0.13;
      // only bounce while actually playing — otherwise rest at scale 1 so the
      // core never freezes mid-pop when paused/stopped
      const bounce = playingRef.current
        ? Math.max(0, 1 - sinceBeat / (beatDurMs * 0.6))
        : 0;
      const color = colorStr(ms, anchor, barDurMs);
      // grows from half size through the build-up, full size from the drop on
      const base = baseScale(ms, anchor, leadStart);
      core.style.background = color;
      core.style.transform = `scale(${base * (1 + amp * bounce)})`;
      core.style.boxShadow = `0 0 ${(8 + amp * bounce * 26) * base}px ${color}`;
    }
    // snap moving elements to whole device pixels — fractional positions make
    // 2px lines anti-alias to varying widths and shimmer while scrolling
    const dpr = window.devicePixelRatio || 1;
    const snap = (px: number) => Math.round(px * dpr) / dpr;
    const w = barWidthRef.current;
    const ov = ovPlayRef.current;
    if (ov)
      ov.style.left = w
        ? `${snap(clamp(ms / duration, 0, 1) * w)}px`
        : `${clamp((ms / duration) * 100, 0, 100)}%`;
    // scroll the zoom track so the playhead stays centred (except at the ends,
    // where the window pins and the playhead walks to the edge)
    const viewStart = clamp(
      ms - zoomSpan / 2,
      0,
      Math.max(0, duration - zoomSpan),
    );
    const track = zoomTrackRef.current;
    if (track)
      track.style.transform = w
        ? `translateX(${-snap((viewStart / zoomSpan) * w)}px)`
        : `translateX(-${(viewStart / duration) * 100}%)`;
    const zp = zoomPlayRef.current;
    if (zp) {
      const frac = clamp((ms - viewStart) / zoomSpan, 0, 1);
      zp.style.left = w ? `${snap(frac * w)}px` : `${frac * 100}%`;
    }
  }, []);

  // keep the px width fresh so paintFrame can pixel-snap positions
  useEffect(() => {
    const el = zoomBarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      barWidthRef.current = el.clientWidth;
      paintFrame(msRef.current);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [paintFrame]);

  // a short synthesized click: accented (bar/downbeat) = higher + louder
  const scheduleClick = useCallback((at: number, accent: boolean) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    // upper half of the mix slider fades the clicks in (song fades out below 50)
    const level = Math.min(1, mixRatioRef.current / 50);
    if (level <= 0) return;
    // a beat whose time already passed is dropped, never played "ASAP" —
    // stacked late clicks are what made scrubbing rapid-fire
    if (at < ctx.currentTime - 0.01) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = accent ? 1660 : 1108;
    const t = Math.max(at, ctx.currentTime);
    gain.gain.setValueAtTime((accent ? 0.5 : 0.28) * level, t);
    gain.gain.exponentialRampToValueAtTime(0.0008, t + 0.07);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.08);
  }, []);

  const handleMixRatio = (value: number) => {
    mixRatioRef.current = value;
    setMixRatio(value);
    // crossfade: both full at the centre, song fades out toward the click end
    if (audioRef.current)
      audioRef.current.volume = Math.min(1, (100 - value) / 50);
  };

  // repaint after any geometry change (tuning) or re-render; re-seat the bar
  // index too so a live alignment change doesn't fire a spurious flash — and
  // re-seat the metronome so clicks follow the NEW grid from here on
  useLayoutEffect(() => {
    lastBarIdxRef.current = Math.floor((msRef.current - anchor) / barDurMs);
    metroSchedToRef.current = msRef.current;
    paintFrame(msRef.current);
  }, [paintFrame, anchor, barDurMs, zoomSpan, duration]);

  // one RAF loop for the component's lifetime — reads geometry/playing from
  // refs, so it never stalls on a re-render or stale closure
  useEffect(() => {
    const loop = () => {
      const audio = audioRef.current;
      if (audio && playingRef.current) {
        // while a seek is settling, currentTime still reports the OLD position —
        // hold the seek target instead of painting/clicking from stale time
        const seeking = audio.seeking;
        let ms = msRef.current;
        if (!seeking) {
          const rawMs = audio.currentTime * 1000;
          const nowPerf = performance.now();
          const off = rawMs - nowPerf;
          if (off > mediaClockOffsetRef.current)
            mediaClockOffsetRef.current = off;
          // if the estimate has drifted well past any plausible staleness, re-seat
          if (mediaClockOffsetRef.current - off > 45)
            mediaClockOffsetRef.current = off;
          ms = nowPerf + mediaClockOffsetRef.current;
        }
        msRef.current = ms;
        paintFrame(ms);
        const sec = Math.floor(ms / 1000);
        if (sec !== lastSecRef.current) {
          lastSecRef.current = sec;
          setTimeMs(ms);
        }
        // emit a ring on each bar crossing, coloured/sized by the core's state
        const { anchor, barDurMs, beatDurMs, leadStart } = geomRef.current;
        const barIdx = Math.floor((ms - anchor) / barDurMs);
        if (barIdx !== lastBarIdxRef.current) {
          lastBarIdxRef.current = barIdx;
          pulseIdRef.current += 1;
          setPulse({
            id: pulseIdRef.current,
            color: colorStr(ms, anchor, barDurMs),
            base: baseScale(ms, anchor, leadStart),
          });
        }
        // metronome: schedule clicks on the tuned beat grid (anchored at the
        // aligned drop) from wherever we last scheduled up to a short lookahead
        const ctx = audioCtxRef.current;
        if (metroOnRef.current && ctx) {
          if (seeking) {
            // don't click against a mid-seek clock; re-seat and resume after
            metroSchedToRef.current = ms;
          } else {
            // clicks become audible one context buffer (baseLatency) after their
            // scheduled time, while the music sample doesn't — schedule them early
            // by that much so click and music hit the ear together
            const baseLatency = ctx.baseLatency || 0;
            const from = Math.max(metroSchedToRef.current, ms);
            const to = ms + METRO_LOOKAHEAD_MS;
            let k = Math.floor((from - anchor) / beatDurMs) + 1;
            for (; anchor + k * beatDurMs <= to; k++) {
              const beatMs = anchor + k * beatDurMs;
              if (beatMs < 0) continue;
              const accent = ((k % 4) + 4) % 4 === 0;
              scheduleClick(
                ctx.currentTime + (beatMs - ms) / 1000 - baseLatency,
                accent,
              );
            }
            metroSchedToRef.current = to;
          }
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [paintFrame, scheduleClick]);

  // keep the ref in sync and settle the core to a resting frame when stopping,
  // so its scale/colour never freeze mid-pop
  useEffect(() => {
    playingRef.current = playing;
    // the media-clock estimate only holds while playing continuously — a pause
    // freezes currentTime against the wall clock, so re-learn it on any toggle
    mediaClockOffsetRef.current = -Infinity;
    if (!playing) paintFrame(msRef.current);
  }, [playing, paintFrame]);

  const seekTo = useCallback(
    (ms: number) => {
      const { duration, anchor, barDurMs } = geomRef.current;
      const t = clamp(ms, 0, duration);
      if (audioRef.current) audioRef.current.currentTime = t / 1000;
      msRef.current = t;
      lastBarIdxRef.current = Math.floor((t - anchor) / barDurMs);
      metroSchedToRef.current = t;
      mediaClockOffsetRef.current = -Infinity;
      setTimeMs(t);
      paintFrame(t);
    },
    [paintFrame],
  );

  const toggleMetronome = () => {
    const next = !metronome;
    if (next) {
      if (!audioCtxRef.current) {
        // created on the toggle click (a user gesture), so it starts unlocked
        audioCtxRef.current = new AudioContext();
      }
      void audioCtxRef.current.resume();
      metroSchedToRef.current = msRef.current; // start clicking from here, no back-fill
    }
    metroOnRef.current = next;
    setMetronome(next);
  };

  // release the click context with the component
  useEffect(
    () => () => {
      void audioCtxRef.current?.close();
    },
    [],
  );

  // start playback at the build-up whenever the drop or lead-in changes
  // (adjusting alignment/phase intentionally does NOT reset the playhead)
  useEffect(() => {
    seekTo(leadStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenDropMs, leadBars]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      // start from where the playhead sits (e.g. the build-up), even if an
      // earlier seek didn't stick before metadata loaded
      audio.currentTime = clamp(msRef.current, 0, duration) / 1000;
      void audio.play();
      setPlaying(true);
    }
  };

  const seekFromClick = (
    e: React.MouseEvent<HTMLDivElement>,
    from: number,
    span: number,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    const t = from + ratio * span;
    if (pickingDrop) {
      // snap to the nearest bar of the tuned grid. The clicked bar already
      // includes the beat-alignment shift, and nublu re-applies that shift on
      // top of chosenDropMs — store the un-shifted value so the effective drop
      // lands exactly on the clicked bar
      const { anchor, barDurMs, duration } = geomRef.current;
      const bar = clamp(
        anchor + Math.round((t - anchor) / barDurMs) * barDurMs,
        0,
        duration,
      );
      onChooseDrop(Math.round(bar - beatPhaseOffset * barDurMs));
      setPickingDrop(false);
      return;
    }
    seekTo(t);
  };

  // track children live in whole-song coordinates (% of the track's width)
  const leadLeftPct = clamp(
    ((anchor - leadBars * barDurMs) / duration) * 100,
    0,
    100,
  );
  const leadWidthPct = clamp(((leadBars * barDurMs) / duration) * 100, 0, 100);

  // bar ticks as one gradient tile per bar, phase-aligned to the tuned grid
  const barPhasePct =
    ((((anchor % barDurMs) + barDurMs) % barDurMs) / barDurMs) * 100;
  const zoomTrackStyle: React.CSSProperties = {
    width: `${(duration / zoomSpan) * 100}%`,
    // 1px solid core with a 1px fade each side: the tick anti-aliases the same
    // way at any sub-pixel position, so no shimmer/width wobble while scrolling
    backgroundImage: `linear-gradient(90deg, transparent 0 calc(${barPhasePct}% - 1.5px), rgba(247, 252, 253, 0.7) calc(${barPhasePct}% - 0.5px) calc(${barPhasePct}% + 0.5px), transparent calc(${barPhasePct}% + 1.5px))`,
    backgroundSize: `${(barDurMs / duration) * 100}% calc(100% - 16px)`,
    backgroundPositionY: "8px",
  };

  return (
    <S.VizStage>
      <audio
        ref={audioRef}
        src={previewUrl}
        preload="auto"
        onEnded={() => setPlaying(false)}
      />

      <S.PulseStage>
        <S.PulseWrap>
          <S.PulseCore ref={coreRef} />
          {pulse && (
            <S.FlashRing
              key={pulse.id}
              style={
                {
                  borderColor: pulse.color,
                  "--ring-base": pulse.base,
                } as React.CSSProperties
              }
            />
          )}
        </S.PulseWrap>
      </S.PulseStage>

      <S.BpmBadge>
        <S.BpmValue>{Math.round(analysis.tempo_bpm)}</S.BpmValue> BPM
      </S.BpmBadge>

      {/* ── Overview: whole song, drops only ── */}
      <S.ScrubBlock>
        <S.ScrubHead>
          <S.RowActions>
            <S.ScrubLabel>Pick a drop</S.ScrubLabel>
            <S.InfoTip>
              <S.InfoTipIcon type="button" aria-label="About drops">
                ?
              </S.InfoTipIcon>
              <S.InfoTipBubble $align="left">
                The drop is the beat of the song that aligns with the
                clip&apos;s first <b>Event</b>.
              </S.InfoTipBubble>
            </S.InfoTip>
          </S.RowActions>
          <S.PickDropButton
            type="button"
            $active={pickingDrop}
            title="Make any bar the drop — arm this, then click a spot on either timeline; the nearest bar becomes the drop"
            onClick={() => setPickingDrop((v) => !v)}
          >
            <FiTarget size={12} />
            {pickingDrop ? "Click a bar…" : "Pick drop"}
          </S.PickDropButton>
        </S.ScrubHead>
        <S.ScrubBar
          $picking={pickingDrop}
          onClick={(e: React.MouseEvent<HTMLDivElement>) =>
            seekFromClick(e, 0, duration)
          }
        >
          <S.ScrubCenterLine />
          {drops.map((d) => (
            <S.DropMarker
              key={d}
              type="button"
              title={`Drop at ${(d / 1000).toFixed(1)}s`}
              $left={(d / duration) * 100}
              $chosen={Math.round(chosenDropMs) === d}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onChooseDrop(d);
              }}
            />
          ))}
          <S.AlignedDrop $left={clamp((anchor / duration) * 100, 0, 100)} />
          <S.PlayLine ref={ovPlayRef} />
        </S.ScrubBar>
      </S.ScrubBlock>

      {/* ── Zoomed: playhead-centred 30s window, bars + drops ── */}
      <S.ScrubBlock>
        <S.ZoomBadge>
          <FiSearch size={12} />
        </S.ZoomBadge>
        <S.ScrubBar
          ref={zoomBarRef}
          $tall
          $picking={pickingDrop}
          onClick={(e: React.MouseEvent<HTMLDivElement>) =>
            seekFromClick(e, zoomViewStart(msRef.current), zoomSpan)
          }
        >
          <S.ZoomTrack ref={zoomTrackRef} style={zoomTrackStyle}>
            <S.LeadBand $left={leadLeftPct} $width={leadWidthPct} />
            <S.ScrubCenterLine />
            {drops.map((d) => (
              <S.DropMarker
                key={d}
                type="button"
                title={`Drop at ${(d / 1000).toFixed(1)}s`}
                $left={(d / duration) * 100}
                $chosen={Math.round(chosenDropMs) === d}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onChooseDrop(d);
                }}
              />
            ))}
            <S.AlignedDrop $left={clamp((anchor / duration) * 100, 0, 100)} />
          </S.ZoomTrack>
          <S.PlayLine ref={zoomPlayRef} />
        </S.ScrubBar>
      </S.ScrubBlock>

      <S.Legend>
        <S.LegendItem>
          <S.LegendSwatch $variant="drop" /> Selected drop
        </S.LegendItem>
        <S.LegendItem>
          <S.LegendSwatch $variant="candidate" /> Detected drop
        </S.LegendItem>
        <S.LegendItem>
          <S.LegendSwatch $variant="beat" /> Bar
        </S.LegendItem>
        <S.LegendItem>
          <S.LegendSwatch $variant="lead" /> Lead up
        </S.LegendItem>
        <S.LegendItem>
          <S.LegendSwatch $variant="playhead" /> Playhead
        </S.LegendItem>
      </S.Legend>

      <S.TransportRow>
        <S.PlayButton onClick={togglePlay} type="button">
          {playing ? <FiPause size={16} /> : <FiPlay size={16} />}
        </S.PlayButton>
        <S.RestartButton
          type="button"
          title="Restart from the lead up"
          onClick={() => seekTo(leadStart)}
        >
          <FiRotateCcw size={15} />
        </S.RestartButton>
        <S.TimeText>
          {formatTime(timeMs)} / {formatTime(duration)}
        </S.TimeText>
        <S.MetronomeButton
          type="button"
          title={metronome ? "Metronome: on" : "Metronome: off"}
          aria-pressed={metronome}
          $active={metronome}
          onClick={toggleMetronome}
        >
          <TbMetronome size={22} />
        </S.MetronomeButton>
        <S.VolumeWrap>
          <S.VolumeButton type="button" title="Metronome / song volume">
            <FiVolume2 size={18} />
          </S.VolumeButton>
          <S.VolumePopup>
            <TbMetronome size={13} />
            <S.VolumeSliderBox>
              <S.VolumeSlider
                type="range"
                min={0}
                max={100}
                value={mixRatio}
                aria-label="Metronome / song volume ratio"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleMixRatio(Number(e.target.value))
                }
              />
            </S.VolumeSliderBox>
            <FiMusic size={13} />
          </S.VolumePopup>
        </S.VolumeWrap>
        <S.InfoTip>
          <S.InfoTipIcon type="button" aria-label="About playback latency">
            ?
          </S.InfoTipIcon>
          <S.InfoTipBubble>
            Wireless audio can lag 100&nbsp;ms+, so the visuals may look ahead
            of the sound — the final render is unaffected. The <b>metronome</b>{" "}
            shares the delay with the music, so judge timing by ear. Accented
            clicks mark the bars where <b>Events</b> land.
          </S.InfoTipBubble>
        </S.InfoTip>
      </S.TransportRow>
    </S.VizStage>
  );
};
